import React, { useEffect, useRef } from "react";
import type { LanguageView, LanguagesView } from "../api.js";
import { LanguageScript } from "../editor/LanguageScript.js";
import { LanguagesPanel } from "../editor/LanguagesPanel.js";

/**
 * The selected version's Languages, in a drawer on the right of the studio.
 * Focus moves into it when it opens and back to what opened it when it
 * closes; Escape closes it. Checking a launch language's script happens here
 * too, since the studio has no script column of its own to show it in.
 */
export const LanguagesDrawer: React.FC<{
  projectId: string;
  versionId: string;
  versionNumber: number;
  view: LanguagesView | null;
  /** The languages could not be read. */
  failed?: boolean;
  onChanged: () => void;
  onClose: () => void;
  /** The language whose script is being checked, or null for the panel. */
  checking: string | null;
  /** The ready version before this one and its languages, offered for this one. */
  previous?: { number: number; languages: LanguageView[] } | null;
  onCheck: (code: string | null) => void;
}> = ({
  projectId,
  versionId,
  versionNumber,
  view,
  failed = false,
  onChanged,
  onClose,
  checking,
  previous,
  onCheck,
}) => {
  const root = useRef<HTMLDivElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    root.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      opener?.focus();
    };
  }, []);

  // Moving between the panel and a script keeps focus inside the dialog.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    root.current?.focus();
  }, [checking]);

  const language = checking ? (view?.languages.find((one) => one.code === checking) ?? null) : null;

  return (
    <>
      <div
        className="fixed inset-0 z-30 bg-black/25"
        aria-hidden="true"
        data-testid="drawer-backdrop"
        onClick={onClose}
      />
      <div
        ref={root}
        role="dialog"
        aria-label={`Languages for v${versionNumber}`}
        tabIndex={-1}
        className="fixed top-0 right-0 bottom-0 z-40 w-[380px] max-w-full outline-none"
      >
        {!view && failed ? (
          <aside
            aria-label="Languages"
            className="flex h-full min-h-0 flex-col gap-3 border-l border-[var(--edge)] bg-bay-900 p-[18px]"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-[16px] font-semibold text-ink-100">Languages</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
                Close
              </button>
            </div>
            <p role="alert" className="text-[13px] text-ink-300">
              Languages couldn't be loaded.
            </p>
            <button type="button" className="btn btn-secondary btn-sm self-start" onClick={onChanged}>
              Try again
            </button>
          </aside>
        ) : language ? (
          <aside
            aria-label={`${language.nativeName} script`}
            className="flex h-full min-h-0 flex-col border-l border-[var(--edge)] bg-bay-900 shadow-[-12px_0_24px_rgba(16,21,28,0.06)]"
          >
            <div className="flex items-center gap-2.5 border-b border-[var(--edge)] px-[18px] py-4">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onCheck(null)}>
                ← Languages
              </button>
              <h2 className="flex-1 truncate text-[14px] font-semibold text-ink-100">
                {language.nativeName}
                {language.status === "review" ? " · Check before voicing" : ""}
              </h2>
              <button type="button" className="btn-icon" aria-label="Close" onClick={onClose}>
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <path d="M2 2 L12 12 M12 2 L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-[18px]">
              <LanguageScript
                key={language.code}
                projectId={projectId}
                version={versionId}
                language={language}
                onChanged={onChanged}
              />
            </div>
          </aside>
        ) : (
          <LanguagesPanel
            key={versionId}
            projectId={projectId}
            version={versionId}
            view={view}
            onChanged={onChanged}
            onClose={onClose}
            onCheckScript={onCheck}
            previous={previous}
            versionNumber={versionNumber}
          />
        )}
      </div>
    </>
  );
};
